/**
 * ChannelHeader - Channel name, description, member count, actions.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';

interface ChannelHeaderProps {
  onSearchClick: () => void;
  onMembersClick: () => void;
  onPinnedClick: () => void;
  onBookmarksClick: () => void;
  onSettingsClick: () => void;
}

const ChannelHeader: React.FC<ChannelHeaderProps> = ({
  onSearchClick,
  onMembersClick,
  onPinnedClick,
  onBookmarksClick,
  onSettingsClick,
}) => {
  const { t } = useTranslation('chat');
  const { currentChannel } = useChat();

  if (!currentChannel) return null;

  const getIcon = (): string => {
    switch (currentChannel.channel_type) {
      case 'direct_message': return '@';
      case 'private': return '~';
      case 'announcement': return '!';
      default: return '#';
    }
  };

  const btnClass = "p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded";

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
      <div className="flex items-center min-w-0">
        <span className="text-gray-400 mr-2 text-lg font-mono">{getIcon()}</span>
        <h3 className="font-semibold text-gray-900 truncate">{currentChannel.name}</h3>
        {currentChannel.is_archived && (
          <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
            {t('archived')}
          </span>
        )}
        {currentChannel.description && (
          <span className="ml-3 text-sm text-gray-500 truncate hidden md:block">
            {currentChannel.description}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {currentChannel.member_count !== undefined && (
          <span className="text-xs text-gray-500">
            {currentChannel.member_count} {t('members')}
          </span>
        )}
        {/* Members */}
        <button onClick={onMembersClick} className={btnClass} title={t('members')}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </button>
        {/* Pinned */}
        <button onClick={onPinnedClick} className={btnClass} title={t('pinnedMessages')}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        </button>
        {/* Bookmarks */}
        <button onClick={onBookmarksClick} className={btnClass} title={t('bookmarks')}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        </button>
        {/* Settings */}
        <button onClick={onSettingsClick} className={btnClass} title={t('channelSettings')}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
        {/* Search */}
        <button onClick={onSearchClick} className={btnClass} title={t('search')}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChannelHeader;
